import { useEffect, useRef } from "react"
import { useLocation } from "react-router-dom"
import { useAuth } from "@/hooks/useAuth"
import { telemetry } from "@/lib/telemetry/client"
import { ehCancelamentoDoNavegador } from "@/lib/telemetry/cancelamento"
import { isStaleChunkError } from "@/lib/app-update"

interface TelemetryProviderProps {
  children: React.ReactNode
}

/**
 * Mount this near the root of the authenticated app. Responsabilidades:
 *
 * 1. Habilita/desabilita o client de telemetria conforme role do usuário
 *    (super_admin = desabilitado por requisito de privacidade)
 * 2. Auto-track de pageviews em route changes
 * 3. Captura erros JS globais (window.error e unhandledrejection)
 * 4. Flush de eventos pendentes em pagehide/beforeunload via sendBeacon
 *
 * Track manual de ações específicas é feito via `useTelemetry()` hook em
 * componentes pontuais (criar lead, mover kanban, enviar mensagem, etc).
 */
export function TelemetryProvider({ children }: TelemetryProviderProps) {
  const { user, role, isAuthenticated } = useAuth()
  const location = useLocation()
  const lastPath = useRef<string | null>(null)

  // Configura o client: ativa pra todos exceto super_admin e usuários sem auth
  useEffect(() => {
    const shouldEnable = isAuthenticated && !!user && role !== "super_admin"
    telemetry.configure({ enabled: shouldEnable })
    // Papel fica no singleton para que o ErrorBoundary — que vive ACIMA deste
    // provider, na raiz do App — consiga dizer quem sofreu a quebra de tela.
    telemetry.setContext({ role: role ?? null })
  }, [isAuthenticated, user, role])

  // Track pageviews on route change
  useEffect(() => {
    if (!telemetry.isEnabled()) return
    if (lastPath.current === location.pathname) return

    telemetry.track({
      event_type: "pageview",
      event_name: "route_change",
      properties: {
        from: lastPath.current,
        search: location.search ? "[stripped]" : "",  // não captura query strings (podem ter PII)
        hash: location.hash ? "[stripped]" : "",
      },
    })
    lastPath.current = location.pathname
  }, [location.pathname, location.search, location.hash])

  // Captura erros JS globais
  useEffect(() => {
    if (typeof window === "undefined") return

    const onError = (event: ErrorEvent) => {
      if (!telemetry.isEnabled()) return
      // Cancelamento do navegador não é erro — nem entra na telemetria da
      // tela. Task wdwxndz6m6.
      if (ehCancelamentoDoNavegador(event.error ?? event.message)) return
      telemetry.track({
        event_type: "error",
        event_name: "error.unhandled",
        properties: {
          message: String(event.message).slice(0, 500),
          filename: event.filename?.slice(0, 300),
          line: event.lineno,
          col: event.colno,
          stack: event.error?.stack?.toString().slice(0, 2000),
          // Fecha a lacuna da versão antiga: import() que falha FORA do
          // ErrorBoundary (ex.: code-splitting manual num hook/handler)
          // também marca stale_chunk, pro relatório diário não contar como
          // erro real — mesma marca que o ErrorBoundary já grava hoje.
          ...(isStaleChunkError(event.error ?? event.message) ? { stale_chunk: true } : {}),
        },
      })
    }

    const onRejection = (event: PromiseRejectionEvent) => {
      if (!telemetry.isEnabled()) return
      const reason = event.reason
      // Cancelamento do navegador (troca de tela, fechar aba, nova navegação
      // antes da resposta) não é erro do sistema. Task wdwxndz6m6 — medido em
      // produção: 125 ocorrências/30 dias de "signal is aborted"/"user
      // aborted" entrando como error.promise sem defeito nenhum por trás.
      if (ehCancelamentoDoNavegador(reason)) return
      const message = reason instanceof Error
        ? reason.message
        : typeof reason === "string"
        ? reason
        : JSON.stringify(reason)
      telemetry.track({
        event_type: "error",
        event_name: "error.promise",
        properties: {
          message: String(message).slice(0, 500),
          stack: reason instanceof Error ? reason.stack?.slice(0, 2000) : null,
          ...(isStaleChunkError(reason) ? { stale_chunk: true } : {}),
        },
      })
    }

    window.addEventListener("error", onError)
    window.addEventListener("unhandledrejection", onRejection)
    return () => {
      window.removeEventListener("error", onError)
      window.removeEventListener("unhandledrejection", onRejection)
    }
  }, [])

  // Flush eventos pendentes ao sair (pagehide funciona melhor que beforeunload em mobile)
  useEffect(() => {
    if (typeof window === "undefined") return
    const flush = () => telemetry.flushBeacon()
    window.addEventListener("pagehide", flush)
    window.addEventListener("beforeunload", flush)
    return () => {
      window.removeEventListener("pagehide", flush)
      window.removeEventListener("beforeunload", flush)
    }
  }, [])

  return <>{children}</>
}
